import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { toast } from 'sonner';
import { useBulkCreateUsers, useBulkValidateUsers } from '@/lib/query/hooks/useUsers';
import { useCompanyAccounts } from '@/lib/query/hooks/useCompanies';
import {
  useMiseSellerGroupsCurrent,
  useMiseSellersCurrent,
} from '@/lib/query/hooks/useMiseSellers';
import { useMiseSellers } from '@/lib/query/hooks/useMiseAccounts';
import { EXCLUDED_SELLER_ID } from '@/lib/types/mise/seller';
import {
  downloadBulkUserCsv,
  generateBulkCreateResultCsv,
  parseBulkUserCsv,
} from '@/lib/utils/csv-bulk-user';
import type {
  BulkCreateUserExecRow,
  BulkCreateUsersRequest,
  BulkCreateUsersResponse,
  BulkValidateUsersRequest,
  BulkValidateUsersResponse,
} from '@/lib/types/mise/user';
import { BulkCreateUserUploadStep, type ComparisonType } from './BulkCreateUserUploadStep';
import { BulkCreateUserReviewStep } from './BulkCreateUserReviewStep';
import { BulkCreateUserResultStep } from './BulkCreateUserResultStep';

interface BulkCreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

type Step = 'upload' | 'review' | 'result';

export function BulkCreateUserDialog({ open, onClose, companyId }: BulkCreateUserDialogProps) {
  const [step, setStep] = useState<Step>('upload');

  // 共通設定
  const [defaultAccountId, setDefaultAccountId] = useState('');
  const [comparisonType, setComparisonType] = useState<ComparisonType>('seller_group_mapping');
  const [comparisonSellerId, setComparisonSellerId] = useState('');
  const [comparisonSellerGroupId, setComparisonSellerGroupId] = useState('');
  const [suppressInvitationEmail, setSuppressInvitationEmail] = useState(false);

  // CSV
  const [fileName, setFileName] = useState('');
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [userNamesByRowIndex, setUserNamesByRowIndex] = useState<Record<number, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // バリデーション・実行結果
  const [validateResult, setValidateResult] = useState<BulkValidateUsersResponse | null>(null);
  const [createResult, setCreateResult] = useState<BulkCreateUsersResponse | null>(null);
  const [validateError, setValidateError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [copiedRowIndex, setCopiedRowIndex] = useState<number | null>(null);

  const validateMutation = useBulkValidateUsers(companyId);
  const createMutation = useBulkCreateUsers(companyId);

  // 確認画面で表示するラベル用に共通設定の選択肢を解決する
  const { data: accounts = [] } = useCompanyAccounts(companyId);
  const { data: sellersCurrent = [] } = useMiseSellersCurrent(defaultAccountId);
  const { data: sellersSnapshot = [] } = useMiseSellers(defaultAccountId);
  const sellers = useMemo(() => {
    const source = sellersCurrent.length > 0 ? sellersCurrent : sellersSnapshot;
    return source.filter((s) => s.id !== EXCLUDED_SELLER_ID);
  }, [sellersCurrent, sellersSnapshot]);
  const { data: sellerGroups = [] } = useMiseSellerGroupsCurrent(defaultAccountId);

  const selectedAccount = accounts.find((acc) => acc.id === defaultAccountId);
  const selectedComparisonSeller = sellers.find((s) => s.id === comparisonSellerId);
  const selectedComparisonSellerGroup = sellerGroups.find((g) => g.id === comparisonSellerGroupId);

  const isPending = validateMutation.isPending || createMutation.isPending;

  const isCommonConfigValid =
    !!defaultAccountId &&
    (comparisonType === 'seller_group_mapping' ||
      (comparisonType === 'seller' && !!comparisonSellerId) ||
      (comparisonType === 'seller_group' && !!comparisonSellerGroupId));

  const validRowCount = validateResult ? validateResult.summary.valid : 0;

  // すべての state をリセットして親に閉じる通知を行う
  const handleClose = () => {
    if (isPending) {
      return;
    }
    setStep('upload');
    setDefaultAccountId('');
    setComparisonType('seller_group_mapping');
    setComparisonSellerId('');
    setComparisonSellerGroupId('');
    setSuppressInvitationEmail(false);
    setFileName('');
    setParseErrors([]);
    setUserNamesByRowIndex({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setValidateResult(null);
    setCreateResult(null);
    setValidateError(null);
    setCreateError(null);
    setCopiedRowIndex(null);
    onClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setParseErrors([]);
    if (!file) {
      setFileName('');
      return;
    }
    setFileName(file.name);
  };

  // 「次へ」: CSV を読み込み + バリデーションAPIを呼び出して Step 2 へ
  const handleProceedToReview = async () => {
    const file = fileInputRef.current?.files?.[0];
    setValidateError(null);
    setParseErrors([]);

    if (!file) {
      setParseErrors(['CSVファイルを選択してください']);
      return;
    }

    let csvText: string;
    try {
      csvText = await file.text();
    } catch (err) {
      setParseErrors([
        err instanceof Error
          ? `CSV読み込みに失敗しました: ${err.message}`
          : 'CSV読み込みに失敗しました',
      ]);
      return;
    }

    const parsed = parseBulkUserCsv(csvText);
    if (parsed.errors.length > 0) {
      setParseErrors(parsed.errors);
      return;
    }
    if (parsed.rows.length === 0) {
      setParseErrors(['CSVに登録対象の行がありません']);
      return;
    }

    const request: BulkValidateUsersRequest = {
      default_account_id: defaultAccountId,
      comparison_seller_id: comparisonType === 'seller' ? comparisonSellerId : null,
      comparison_seller_group_id:
        comparisonType === 'seller_group' ? comparisonSellerGroupId : null,
      use_seller_group_mapping: comparisonType === 'seller_group_mapping',
      rows: parsed.rows.map(({ row_index, user_email, seller_id }) => ({
        row_index,
        user_email,
        seller_id,
      })),
    };

    try {
      const result = await validateMutation.mutateAsync(request);
      setUserNamesByRowIndex(
        Object.fromEntries(parsed.rows.map((r) => [r.row_index, r.user_name])),
      );
      setValidateResult(result);
      setStep('review');
    } catch (err) {
      console.error('一括バリデーションに失敗しました:', err);
      setValidateError(
        err instanceof Error ? err.message : 'バリデーション中にエラーが発生しました',
      );
    }
  };

  // 「N件を登録」: 実行APIを呼び Step 3 へ
  const handleExecuteCreate = async () => {
    if (!validateResult) return;
    setCreateError(null);

    const okRows = validateResult.results.filter((r) => r.status === 'ok');
    const execRows: BulkCreateUserExecRow[] = okRows.map((r) => ({
      row_index: r.row_index,
      user_email: r.user_email,
      user_name: userNamesByRowIndex[r.row_index],
      seller_id: r.seller_id,
    }));

    const request: BulkCreateUsersRequest = {
      default_account_id: defaultAccountId,
      comparison_seller_id: comparisonType === 'seller' ? comparisonSellerId : null,
      comparison_seller_group_id:
        comparisonType === 'seller_group' ? comparisonSellerGroupId : null,
      use_seller_group_mapping: comparisonType === 'seller_group_mapping',
      suppress_invitation_email: suppressInvitationEmail || undefined,
      rows: execRows,
    };

    try {
      const result = await createMutation.mutateAsync(request);
      setCreateResult(result);
      setStep('result');
    } catch (err) {
      console.error('一括登録に失敗しました:', err);
      setCreateError(err instanceof Error ? err.message : '一括登録中にエラーが発生しました');
    }
  };

  const handleDownloadResultCsv = () => {
    if (!createResult) return;
    const csv = generateBulkCreateResultCsv(createResult.results);
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\..*$/, '')
      .replace('T', '_');
    downloadBulkUserCsv(`mise_users_bulk_result_${timestamp}.csv`, csv);
  };

  const handleCopyPassword = async (rowIndex: number, password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedRowIndex(rowIndex);
      setTimeout(() => setCopiedRowIndex((v) => (v === rowIndex ? null : v)), 2000);
    } catch (err) {
      console.error('クリップボードへのコピーに失敗しました:', err);
      toast.error('クリップボードへのコピーに失敗しました');
    }
  };

  const defaultAccountLabel = selectedAccount
    ? selectedAccount.name || selectedAccount.id
    : defaultAccountId;
  const comparisonSellerLabel = selectedComparisonSeller?.name || comparisonSellerId;
  const comparisonSellerGroupLabel = selectedComparisonSellerGroup?.name || comparisonSellerGroupId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {step === 'upload' && 'CSVで一括登録 - 設定とCSVアップロード'}
        {step === 'review' && 'CSVで一括登録 - 確認'}
        {step === 'result' && 'CSVで一括登録 - 結果'}
      </DialogTitle>
      <DialogContent>
        {step === 'upload' && (
          <BulkCreateUserUploadStep
            companyId={companyId}
            isPending={isPending}
            defaultAccountId={defaultAccountId}
            setDefaultAccountId={setDefaultAccountId}
            comparisonType={comparisonType}
            setComparisonType={setComparisonType}
            comparisonSellerId={comparisonSellerId}
            setComparisonSellerId={setComparisonSellerId}
            comparisonSellerGroupId={comparisonSellerGroupId}
            setComparisonSellerGroupId={setComparisonSellerGroupId}
            suppressInvitationEmail={suppressInvitationEmail}
            setSuppressInvitationEmail={setSuppressInvitationEmail}
            fileName={fileName}
            parseErrors={parseErrors}
            validateError={validateError}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
          />
        )}

        {step === 'review' && validateResult && (
          <BulkCreateUserReviewStep
            validateResult={validateResult}
            userNamesByRowIndex={userNamesByRowIndex}
            defaultAccountLabel={defaultAccountLabel}
            comparisonType={comparisonType}
            comparisonSellerLabel={comparisonSellerLabel}
            comparisonSellerGroupLabel={comparisonSellerGroupLabel}
            suppressInvitationEmail={suppressInvitationEmail}
            createError={createError}
          />
        )}

        {step === 'result' && createResult && (
          <BulkCreateUserResultStep
            createResult={createResult}
            copiedRowIndex={copiedRowIndex}
            onCopyPassword={handleCopyPassword}
          />
        )}
      </DialogContent>
      <DialogActions>
        {step === 'upload' && (
          <>
            <Button onClick={handleClose} disabled={isPending}>
              キャンセル
            </Button>
            <Button
              onClick={handleProceedToReview}
              variant="contained"
              disabled={isPending || !isCommonConfigValid || !fileName}
              startIcon={validateMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {validateMutation.isPending ? '検証中...' : '次へ'}
            </Button>
          </>
        )}
        {step === 'review' && (
          <>
            <Button onClick={() => setStep('upload')} disabled={isPending}>
              戻る
            </Button>
            <Button onClick={handleClose} disabled={isPending}>
              キャンセル
            </Button>
            <Button
              onClick={handleExecuteCreate}
              variant="contained"
              disabled={isPending || validRowCount === 0}
              startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {createMutation.isPending ? '登録中...' : `${validRowCount}件を登録`}
            </Button>
          </>
        )}
        {step === 'result' && (
          <>
            <Button onClick={handleDownloadResultCsv} disabled={!createResult}>
              結果CSVをダウンロード
            </Button>
            <Button onClick={handleClose} variant="contained">
              閉じる
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
